/**
 * Map Stride admin API payloads to app types (handles naming variants).
 */

import type { User } from '@/types'
import type { DashboardData, Interest } from '@/types/dashboard'

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return fallback
}

export function mapAdminDashboardToDashboardData(raw: unknown): DashboardData {
  const o = unwrapPayload(raw)
  const counts =
    o.counts && typeof o.counts === 'object' && !Array.isArray(o.counts)
      ? (o.counts as Record<string, unknown>)
      : {}

  return {
    stats: {
      registeredUsers: num(
        counts.users ??
          o.users ??
          o.registeredUsers ??
          o.totalUsers ??
          o.userCount ??
          o.total_user_count
      ),
      usersRegisteredToday: num(
        counts.newUsersToday ??
          o.newUsersToday ??
          o.usersRegisteredToday ??
          o.signupsToday ??
          o.users_registered_today
      ),
    },
  }
}

/** Unwrap Stride `{ success, data }` envelopes */
function unwrapPayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {}
  const root = data as Record<string, unknown>
  const inner = root.data
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return root
}

const INTEREST_LIST_KEYS = [
  'items',
  'content',
  'results',
  'interests',
  'records',
  'rows',
  'list',
  'data',
] as const

/** Find the first non-empty interest array inside paginated API envelopes */
function findInterestArray(data: unknown, depth = 0): unknown[] {
  if (!data || depth > 5) return []
  if (Array.isArray(data)) return data
  if (typeof data !== 'object') return []

  const o = data as Record<string, unknown>

  for (const key of INTEREST_LIST_KEYS) {
    const val = o[key]
    if (Array.isArray(val)) return val
    if (val && typeof val === 'object') {
      const nested = findInterestArray(val, depth + 1)
      if (nested.length > 0) return nested
    }
  }

  return []
}

export function mapInterestDto(raw: unknown): Interest {
  const o = unwrapPayload(raw)
  const iconUrl = o.iconUrl ?? o.icon_url
  const imageUrl = o.imageUrl ?? o.image_url
  const url =
    typeof iconUrl === 'string'
      ? iconUrl
      : typeof imageUrl === 'string'
        ? imageUrl
        : undefined
  const statusRaw = o.status
  const status: 'active' | 'inactive' =
    statusRaw === 'inactive' || statusRaw === 'INACTIVE' ? 'inactive' : 'active'
  const id = String(o.id ?? o.interestId ?? o.interest_id ?? '')
  return {
    id,
    name: String(o.name ?? ''),
    imageUrl: url,
    icon: typeof o.icon === 'string' ? o.icon : url,
    status,
    createdAt:
      typeof o.createdAt === 'string'
        ? o.createdAt
        : typeof o.created_at === 'string'
          ? o.created_at
          : undefined,
  }
}

export function extractInterestList(data: unknown): Interest[] {
  const rows = findInterestArray(data)
  return rows.map(mapInterestDto).filter(i => Boolean(i.id) || Boolean(i.name))
}

export interface AdminProfileDto {
  id: string
  email: string
  name: string
  createdAt?: string
  updatedAt?: string
}

/** Map GET/PATCH /admin/me response to profile DTO */
export { mapAdminProfileDto } from '@/data/profile/mappers/profile.mapper'

export function mapAdminProfileToUser(dto: AdminProfileDto | unknown): User {
  const raw =
    dto && typeof dto === 'object' && 'id' in dto
      ? (dto as AdminProfileDto)
      : unwrapPayload(dto)
  const d = raw as unknown as AdminProfileDto
  return {
    id: String(d.id ?? ''),
    email: String(d.email ?? ''),
    name: String(d.name ?? d.email ?? 'Admin'),
    role: 'admin',
    createdAt: d.createdAt ?? new Date().toISOString(),
  }
}

/** Normalize POST /admin/auth/login (and similar) to stored auth shape */
export function normalizeAdminLoginResponse(data: unknown): {
  token: string
  expiresIn: number
  user: User
} | null {
  if (!data || typeof data !== 'object') return null
  const d = unwrapPayload(data)
  const token = (d.token ?? d.accessToken ?? d.access_token) as string | undefined
  const expiresIn = num(d.expiresIn ?? d.expires_in, 3600)
  const admin = d.admin as Record<string, unknown> | undefined
  const userBlock = (d.user ?? admin) as Record<string, unknown> | undefined
  if (!token || !userBlock) return null
  const user: User = {
    id: String(userBlock.id ?? ''),
    email: String(userBlock.email ?? ''),
    name: String(userBlock.name ?? userBlock.email ?? 'Admin'),
    role: 'admin',
    createdAt:
      typeof userBlock.createdAt === 'string'
        ? userBlock.createdAt
        : new Date().toISOString(),
  }
  return { token, expiresIn, user }
}
